import { TicketPriceRestriction } from '@vista-digital/ocapi-types/v1';
import { logger } from '../../../../utils/logger';
import { isMemberSignedIn } from '../../../loyalty/selectors/member/is-member-signed-in';
import { getMaximumAllowedMemberRewards } from '../../../loyalty/utils/member-reward-utils';
import { getOrderTicketRedemptionCardRedemptionDetails } from '../../../order/selectors/ticket-redemption-cards/get-order-ticket-redemption-card-redemption-details';
import { getOrderTicketRedemptionCardsForSubscription } from '../../../order/selectors/ticket-redemption-cards/get-order-ticket-redemption-cards-for-subscription';
import { getOrderTicketRedemptionCardsForThirdPartyMemberScheme } from '../../../order/selectors/ticket-redemption-cards/get-order-ticket-redemption-cards-for-third-party-member-scheme';
import { isVoucherDiscountAvailable } from '../voucher-utils';
export const isMemberTicketPrice = (price) => {
    return price.restrictions.includes(TicketPriceRestriction.Member) || isMemberRewardTicketPrice(price);
};
export const isMemberRewardTicketPrice = (price) => {
    return price.restrictions.includes(TicketPriceRestriction.MemberReward);
};
export const isMemberSubscriptionTicketPrice = (price) => {
    return price.restrictions.includes(TicketPriceRestriction.MemberSubscriptionCard);
};
export const isVoucherTicketPrice = (price) => {
    return price.restrictions.includes(TicketPriceRestriction.Voucher);
};
export const isThirdPartyMemberTicketPrice = (price) => {
    return price.restrictions.includes(TicketPriceRestriction.ThirdPartyMemberCard);
};
export const isDiscountTicketPrice = (price) => {
    return price.restrictions.includes(TicketPriceRestriction.Discount);
};
export const isTicketPriceAvailable = (price) => {
    const exclusiveRestrictions = price.restrictions.filter(isRestrictionExclusive);
    if (exclusiveRestrictions.length > 1) {
        logger.warn(`Cannot sell ticket '${price.ticketTypeId}' as it has multiple exclusive restrictions: ${exclusiveRestrictions.join(', ')}.`);
        return false;
    }
    return price.restrictions.every(restriction => {
        switch (restriction) {
            case TicketPriceRestriction.Member:
                return isMemberSignedIn();
            case TicketPriceRestriction.MemberReward:
                return isMemberRewardPriceAvailable(price);
            case TicketPriceRestriction.MemberSubscriptionCard:
                return isMemberSubscriptionTicketPriceAvailable(price);
            case TicketPriceRestriction.Voucher:
                return isVoucherDiscountAvailable(price.voucherTypeCodes);
            case TicketPriceRestriction.ThirdPartyMemberCard:
                return isThirdPartyMemberTicketPriceAvailable(price);
            default:
                logger.warn(`Cannot sell ticket '${price.ticketTypeId}' with unknown restriction '${restriction}'.`);
                return false;
        }
    });
};
/**
 * We do not support combinations of some restrictions, so we consider them to be exclusive.
 * If a ticket price has multiple exclusive restrictions, we filter it out.
 */
const isRestrictionExclusive = (restriction) => {
    switch (restriction) {
        case TicketPriceRestriction.MemberReward:
        case TicketPriceRestriction.Voucher:
        case TicketPriceRestriction.ThirdPartyMemberCard:
        case TicketPriceRestriction.MemberSubscriptionCard:
            return true;
        default:
            return false;
    }
};
const isMemberRewardPriceAvailable = (price) => {
    return !!getMaximumAllowedMemberRewards([price.rewardId]);
};
const isThirdPartyMemberTicketPriceAvailable = (price) => {
    const cards = getOrderTicketRedemptionCardsForThirdPartyMemberScheme(price.thirdPartyMemberSchemeId);
    return cards.some(isTicketRedemptionCardAvailable);
};
const isMemberSubscriptionTicketPriceAvailable = (price) => {
    const cards = getOrderTicketRedemptionCardsForSubscription(price.subscriptionId);
    return cards.some(isTicketRedemptionCardAvailable);
};
const isTicketRedemptionCardAvailable = (ticketRedemptionCard) => {
    const { redemptionLimit } = getOrderTicketRedemptionCardRedemptionDetails(ticketRedemptionCard.id);
    return redemptionLimit.isUnlimited || redemptionLimit.maximum > 0;
};
